import _ from 'lodash';
import moment from 'moment';
import { extractByRegex, replaceWithData } from '../../../utils/string-helpers';
import { formatCurrency } from '@fortress-technology-solutions/fortress-component-library/utils';
import { data as part5Headers } from './part5';

const CAPITAL_HEADER_EXCLUDES = [
  '0', // Appliances
  '5', // Heating & AC
  '14', // Total
];

function createCapitalItemsText(
  entries: Array<[string, string | number]>,
  targetValue: string,
) {
  const items: Array<Object> = entries
    .reduce((acc, [key, value]) => {
      const lastHyphenIndex = key.lastIndexOf('-');
      const coordinate = key.slice(0, lastHyphenIndex);
      const name = key.slice(lastHyphenIndex + 1);
      const obj = { [name]: value, field: coordinate };
      const data = acc.find((d) => d.field === coordinate);

      if (data) {
        _.assign(data, obj);
      } else {
        acc.push(obj);
      }

      return acc;
    }, [])
    .filter((data) => parseFloat(data[targetValue] || 0) !== 0);
  const list = items.map((data) => {
    const fieldParts = data.field.split('-');

    const result = [
      // Indent spaces in the same way as variance
      `      ${formatCurrency(parseFloat(data[targetValue]))}`,
      `${part5Headers[fieldParts[1]].header}/${
        part5Headers[fieldParts[1]].rows[fieldParts[2]].label
      }`,
    ];

    if (data.comments) {
      result.push(data.comments);
    }

    return result.join(' - ');
  });

  return list.join('\n');
}

export function calculateFields({
  formValues,
  property,
  rentForecast,
  vacancyRateActual,
}) {
  return {
    commP2L20: _.get(formValues, '1-2-1-comments') || '',
    currentRaUnits: _.get(property, 'setup.RDTotalRAUnits') || 0,
    PropertyN: _.get(property, 'name') || '',
    SiteCity: _.get(property, 'physicalCity') || '',
    SiteState: _.get(property, 'physicalState') || '',
    TodayDate: moment().format('MM/DD/YYYY'),
    pbBegDate: `01/01/${parseInt(rentForecast.year, 10) + 1}`,
    proposedVacancyRate: _.get(rentForecast, 'vacancyRate') || 0,
    currentVacancyRate: vacancyRateActual,
    currentYear: moment().format('YYYY'),
    currentYearShort: moment().format('YY'),
  };
}

export const functions = {
  nUnitsProperty: ({ fpRows }) =>
    fpRows.reduce((total, row) => {
      return total + row.numUnits.value;
    }, 0),
  brSize: ({ fpRows }) =>
    fpRows
      .map((row) => {
        return row.bedsBaths.value.split('/')[0];
      })
      .join(', '),
  projectRentalType: ({ property }) =>
    _.get(property, 'setup.RDProjectRentalTypes', [])
      .map((type) => type.name)
      .join(', '),
  proposedBasicRent: ({ fpRows, changeType }) => {
    // All RD floor plans will have the same basic proposed value
    const value =
      _.get(
        fpRows,
        '[0].newbasicRentProposedChange.value.props.defaultValue',
      ) || 0;
    const indexValue = value === 0 ? 0 : 1;
    const text = functionValues.proposedBasicRent[indexValue];

    if (indexValue === 0) {
      return text;
    }

    return replaceWithData(text, {
      displayedValue:
        changeType === 'dollar' ? formatCurrency(Math.abs(value)) : `${value}%`,
      signChange: value < 0 ? 'decrease' : 'increase',
    });
  },
  uaProposedBr: ({ fpRows }) =>
    fpRows
      .map((fpRow) => {
        const numBrs = fpRow.bedsBaths.value.split('/')[0];
        const currentValue = fpRow['currentRD Utility Allowance']?.value || 0;
        const newValue = fpRow['newRD Utility Allowance']?.value || 0;
        const indexValue = newValue !== 0 ? 0 : 1;
        const resultText = replaceWithData(
          functionValues.proposedUA2[indexValue],
          {
            CurrentUAValue: formatCurrency(currentValue),
            ProposedUAValue: formatCurrency(newValue),
            fpBr: numBrs,
          },
        );

        return (
          `Per the 12-month historical utility averages, there has been ${functionValues.proposedUA1[indexValue]} ` +
          `significant change in the utility usage for the ${numBrs} BR. ${resultText}`
        );
      })
      .join('\n\n'),
  varianceThresholds: ({ formValues }) =>
    [
      {
        line: '11',
        value: formValues['1-0-10-variancePercentage'],
        comment: formValues['1-0-10-comments'],
      },
      {
        line: '18',
        value: formValues['1-1-6-variancePercentage'],
        comment: formValues['1-1-6-comments'],
      },
      {
        line: '33',
        value: formValues['1-2-14-variancePercentage'],
        comment: formValues['1-2-14-comments'],
      },
      {
        line: '40',
        value: formValues['1-3-6-variancePercentage'],
        comment: formValues['1-3-6-comments'],
      },
    ]
      .map((variance) => {
        const indexValue = Math.abs(variance.value) < 10 ? 0 : 1;
        const doesValue = functionValues.variableDoes[indexValue];
        const textValue = functionValues.varianceThreshold[indexValue];

        // Spacing is intended
        return replaceWithData(`      ${textValue}`, {
          part2Line: variance.line,
          variableDoes: doesValue,
          part2LineComment: variance.comment || '*****NO_COMMENT_SET*****',
        });
      })
      .join('\n'),
  administrativeCosts: ({ formValues }) => {
    const partIILine33ProposedValue =
      parseFloat(formValues['1-2-14-proposedBudget']) || 0;
    const partILine10ProposedValue =
      parseFloat(formValues['0-0-9-proposedBudget']) || 0;
    const percentage = partIILine33ProposedValue / partILine10ProposedValue;
    const percentageDisplay = (percentage * 100).toFixed(2);
    const indexValue = percentage < 0.23 ? 0 : 1;

    return (
      `The projected administrative expense calculates out to be ${percentageDisplay}%. ` +
      functionValues.basicPotentialThreshold[indexValue]
    );
  },
  operatingExpenses: ({ formValues }) => {
    const targetValue = 'operatingProposed';
    const administrativeAccountType = 'operating';
    const entries = _.entries(formValues).filter(([key]) => {
      const [partIndex, groupIndex] = key.split('-');
      return (
        partIndex === '3' && // Part 5
        !CAPITAL_HEADER_EXCLUDES.includes(groupIndex) && // Header group
        (_.endsWith(key, targetValue) || _.endsWith(key, 'comments'))
      );
    });
    const hasExpenses = entries.some(([key, value]) => {
      return _.endsWith(key, targetValue) && parseFloat(value) !== 0;
    });

    if (!hasExpenses) {
      return replaceWithData(functionValues.administrativeCosts[0], {
        administrativeAccountType,
      });
    }

    const initialText = replaceWithData(functionValues.administrativeCosts[1], {
      administrativeAccountType,
    });
    const costList = createCapitalItemsText(entries, targetValue);

    return `${initialText}\n${costList}`;
  },
  reservedExpenses: ({ formValues }) => {
    const targetValue = 'reserveProposed';
    const administrativeAccountType = 'replacement reserve';
    const entries = _.entries(formValues).filter(([key]) => {
      const [partIndex, groupIndex] = key.split('-');
      return (
        partIndex === '3' && // Part 5
        !CAPITAL_HEADER_EXCLUDES.includes(groupIndex) && // Header group
        (_.endsWith(key, targetValue) || _.endsWith(key, 'comments'))
      );
    });
    const hasExpenses = entries.some(([key, value]) => {
      return _.endsWith(key, targetValue) && parseFloat(value) !== 0;
    });

    if (!hasExpenses) {
      return replaceWithData(functionValues.administrativeCosts[0], {
        administrativeAccountType,
      });
    }

    const initialText = replaceWithData(functionValues.administrativeCosts[1], {
      administrativeAccountType,
    });
    const costList = createCapitalItemsText(entries, targetValue);

    return `${initialText}\n${costList}`;
  },
  managementStateFee: ({ formValues }) => {
    // Extract from the first number on the equation from P2L20 comment
    const [fee] = extractByRegex(formValues['1-2-1-comments'], /\d+/);

    return `$${fee ? fee : 0}`;
  },
  assetSummary: ({ property, fpRows }) => {
    const affordablePrograms = property.pap.filter(
      (program) => program.masterAffordableProgram.name !== 'RD',
    );
    const hasLessRevenueUnits =
      _.sum(
        fpRows
          .filter((row) => row.unitType.value !== 'Z')
          .map((row) => row.numUnits.value),
      ) < 16; // Less than 16 to be eligible for fee

    // Don't display summary if none of the conditions pass
    if (!affordablePrograms.length && !hasLessRevenueUnits) {
      return '';
    }

    const parts = [];

    if (hasLessRevenueUnits) {
      parts.push(functionValues.assetTexts[0]);
    }

    if (affordablePrograms.length) {
      parts.push(
        replaceWithData(functionValues.assetTexts[1], {
          applicableAffordablePrograms: affordablePrograms
            .map((program) => program.masterAffordableProgram.name)
            .join(', '),
        }),
      );
    }

    return `In addition, the asset ${parts.join(' and ')}. ${
      functionValues.assetTexts[2]
    }`;
  },
};

export const functionValues = {
  proposedBasicRent: [
    'no change in rental rate',
    'a displayedValue rental rate signChange',
  ],
  proposedUA1: ['a', 'no'],
  proposedUA2: [
    'The utility allowance will change from CurrentUAValue to ' +
      'ProposedUAValue for the fpBr BR.',
    'The utility allowance will remain CurrentUAValue for the fpBr BR.',
  ],
  variableDoes: ['does not', 'does'],
  varianceThreshold: [
    'Part II, Line part2Line variableDoes exceed the 10% variance from the prior budget',
    'Part II, Line part2Line variableDoes exceed the 10% variance from the prior budget due to part2LineComment',
  ],
  basicPotentialThreshold: [
    'This does not exceed the 23% threshold of gross potential basic rent.',
    'This does exceed the 23% threshold of gross potential rent due to increases in management fees, health ' +
      'insurance, etc. *****We determine the list by adding items that the proposed increased above the current for ' +
      'the new proposed budget. This list almost always includes management fees, health insurance and site ' +
      'management payroll.*****',
  ],
  administrativeCosts: [
    'We are proposing no capital expenditures from the administrativeAccountType account.',
    'We are proposing capital expenditures from the administrativeAccountType account.',
  ],
  assetTexts: [
    'has less than 16 units and is eligible for an add-on fee of $5.00',
    'is layered with applicableAffordablePrograms and is eligible for an add-on fee of ($5.00 per program)',
    'This add-on is applicable to all revenue producing units regardless of the occupancy status ' +
      'and has been included in the total management fee calculation.',
  ],
};

export const rawTemplate =
  '((PropertyN)) is a [[nUnitsProperty]] unit [[projectRentalType]] ' +
  'complex located in ((SiteCity)), ((SiteState)). It has [[nUnitsProperty]] ' +
  'total units with ((currentRaUnits)) of rental assistance slots. The bedroom sizes offered ' +
  'by the asset are [[brSize]].\n' +
  '\n' +
  'Please note this proposed budget is planned to go into effect ((pbBegDate)), with ' +
  '[[proposedBasicRent]]. We are proposing a budget that will create a firm basis ' +
  'from which all agency requirements can successfully be met in the future.\n' +
  '\n' +
  'MINC Findings as of ((TodayDate)). *****list findings with date completed, date scheduled, ' +
  'or reason why correction is not scheduled and plan for completion in new proposed budget.*****\n' +
  '\n' +
  'We are proposing a ((proposedVacancyRate))% vacancy rate. The current vacancy rate is ' +
  '((currentVacancyRate)), and *****give details on why the proposed is lower/higher if ' +
  'needed. The proposed/approved workout agreement will be submitted with the emailed copy ' +
  'of the proposed budget. (use last line only if rate is 10% or greater)*****\n' +
  '\n' +
  '[[uaProposedBr]]\n' +
  '\n' +
  'These allowances are reflected in Part IV of this budget proposal.\n' +
  '\n' +
  'The anticipated management fee for this proposed budget is calculated using a 100% occupancy ' +
  'rate and the USDA Rural Development approved management for ((SiteState)) of [[managementStateFee]] ' +
  'per unit. [[assetSummary]] The calculation for Part 2, Line 20 is ((commP2L20)).\n' +
  '\n' +
  '[[administrativeCosts]]\n' +
  '\n' +
  '*****Part II, Line32 increased due to strategic investments in operational efficiency and IT support. ' +
  'We purchased new property management and accounting software and hired a consultant to assist with its ' +
  'implementation. Additionally, we onboarded a new IT company to manage and improve our technology ' +
  'infrastructure.*****\n' +
  '\n' +
  'Subtotal 10% Threshold Variance\n' +
  '[[varianceThresholds]]\n' +
  '\n' +
  'Planned capital expenditures are addressed in Part V of the budget. During the next year, any appliances ' +
  'or HVAC will be replaced on an as needed basis.\n' +
  '\n' +
  '[[operatingExpenses]]\n' +
  '\n' +
  '[[reservedExpenses]]\n' +
  '\n' +
  '*****Please place a detailed description, including building numbers, on any O&M or R/R capital expenditure ' +
  'items other than appliances, flooring, & HVAC on Part V.*****\n' +
  '\n' +
  '((currentYear)) operating capital budget items which will be completed by ((currentYear)) ' +
  'year end are as follows.\n*****[LIST ANY ((currentYear)) CAPITAL ITEMS NOT COMPLETED AS ' +
  'OF 6/30/((currentYearShort)) SCHEDULED BEFORE YEAR END]*****\n' +
  '\n' +
  '((currentYear)) operating capital budget items which will be completed by ((currentYear)) ' +
  'year end are as follows.\n*****[LIST ANY ((currentYear)) CAPITAL ITEMS NOT COMPLETED AS ' +
  'OF 6/30/((currentYearShort)) SCHEDULED BEFORE YEAR END]*****\n' +
  '\n' +
  '((currentYear)) operating capital budget items which will not be completed before year end are as follows. \n' +
  '*****[LIST ANY ((currentYear)) CAPITAL ITEMS NOT COMPLETED AS OF 6/30/((currentYearShort)) ' +
  'NOT SCHEDULED BEFORE YEAR END]*****\n' +
  '\n' +
  '((currentYear)) reserve capital budget items which will not be completed before year end are as follows. \n' +
  '*****[LIST ANY 2025 CAPITAL ITEMS NOT COMPLETED AS OF 6/30/((currentYearShort)) NOT SCHEDULED BEFORE YEAR ' +
  'END]*****\n' +
  '\n' +
  'ON FILE WITH USDA RURAL DEVELOPMENT\n' +
  'The MANAGEMENT CERTIFICATION, the IDENTITY OF INTEREST, the MANAGEMENT PLAN, and the ATTACHMENT 3B ' +
  'are up to date/expire at the end of the year. The updated forms will be included with the emailed ' +
  'copy of the budget.\n' +
  '\n' +
  'For any questions related to this proposed budget, please contact **** at ****';
